export type {
  LlmAgentAgentV1ActionContainerRequest,
  LlmAgentAgentV1CostContent,
  LlmAgentAgentV1MergeClipsRequestAudio,
  LlmAgentAgentV1QueryCardContent,
  StudioV1ClipProject,
  StudioV1OutputMediaConfig,
  StudioV1Timeline,
  ViduV1AgentShowCase,
  ViduV1Content,
  ViduV1ExploreDetailProReply,
  ViduV1GetTaskStateReply,
  ViduV1ImageContent,
  ViduV1MediaAssetDetail,
  ViduV1Message,
  ViduV1MiniVideoDetail,
  ViduV1ScriptContent,
  ViduV1ShortFilm,
  ViduV1Tutorial,
} from '@/app/services/vidu';
export * from './activity';
export * from './agent';
export * from './agent-chat';
export * from './claw';
export * from './community';
export * from './create-tool';
export * from './creation';
export * from './element-dialog';
export * from './feature-tour';
export * from './free-credits-task-dialog';
export * from './global-modal';
export * from './live';
export * from './plan';
export * from './post-reference';
export * from './report';
export * from './sanity';
export * from './task-form';
export * from './text-prompt';
export * from './upload';
export * from './user-info';
export * from './video-player';

import type { FreeCreditsTaskName } from '@/app/constants';
import type { CreditV1Coupon, CreditV1NexusHistory } from '@/app/services/credit';
import type { _Error, IamV1User } from '@/app/services/iam';
import type { ViduV1Element, ViduV1GetActivityTemplateReply, ViduV1TagInfo, ViduV1Task } from '@/app/services/vidu';
import type en from '../../../messages/en.json';
import type { UpdateInteractionStatusParams } from './community';
import type { SubsPlanType } from './plan';
import type {
  TaskSettingsQ1,
  TaskSettingsQ2,
  TaskSettingsQ2_pro,
  TaskSettingsQ3,
  TaskSettingsText2Audio,
  TaskSettingsV2_0,
} from './task-form';
import type { TextPromptMentionMediaType, TextPromptWithInputItem } from './text-prompt';
import type { FormImagePrompt, FormVideoPrompt, ImagePromptWithSize, ImageRange } from './upload';

type Messages = typeof en;

declare global {
  interface IntlMessages extends Messages {}
  interface Window {
    fbq?: (command: 'init' | 'track', eventName: string, params?: Record<string, string | number>) => void;
    _fbq?: unknown;
  }
}

export type CollectionTag = ViduV1TagInfo;

// https://shengshu.feishu.cn/wiki/LgrAwU6uviO4gSkA64fcLg08nFf
export type ServiceError = _Error;

export type UserInfo = IamV1User;

export type ElementInfo = ViduV1Element;

/** 优惠券类型（供 CouponDialog、PurchaseCredits 等公用组件使用） */
export type CouponType = CreditV1Coupon;

export type TemplateSettings = {
  sample_count?: number;
  aspect_ratio?: string;
  has_audio?: boolean;
};

export enum EllipsisActionTypeEnum {
  Report = 'Report', // 反馈
  Delete = 'Delete', // 删除
  InspirationReport = 'InspirationReport', // 灵感举报
  T2AFeedback = 'T2AFeedback',
}

export type ActionDialogType = Exclude<EllipsisActionTypeEnum, 'Delete'>;

export type DialogObjectType = 'creation' | 'task' | 'share_element' | 'live';

export type SearchOption = 'all' | 'favorite';
export type SearchOptionWithDetail = `${SearchOption}/detail`;

// 可创建的模型版本
export type ModelVersion =
  | '3.2'
  | '3.2_pro_m'
  | '3.2_fast_m'
  | '3.2_lite_m'
  | '3.2_image_2'
  | '3.2_a'
  | '3.2_hh'
  | '3.1_pro'
  | '3.1'
  | '3.0'
  | '2.0';
// 可展示的模型版本（含仅展示不可创建的版本）
export type DisplayModelVersion = ModelVersion;
export type FormSettingsKey =
  | 'settingsV2_0'
  | 'settingsQ1'
  | 'settingsQ2'
  | 'settingsQ2_pro'
  | 'settingsQ3'
  | 'settingsQ3_pro'
  | 'settingsQ3_fast'
  | 'settingsQ3_lite'
  | 'settingsGPTImage2'
  | 'settingsQ3_a'
  | 'settingsQ3_hh';
// 包含已经下线的模型版本
export type AllModelVersion = DisplayModelVersion | '1.0' | '1.5';

export type RADIO_OPTIONS_SIDE_TYPE = 'top' | 'right' | 'bottom' | 'left';

export interface ImageResolution {
  width: number;
  height: number;
}

export type SiteType = 'china' | 'global';

export type ReferencePrompt =
  | (FormImagePrompt & { type: 'image'; name: string })
  | (FormVideoPrompt & { type: 'video'; name: string })
  | {
      type: 'element';
      id: string;
      name: string;
      status: 'forbidden' | 'done' | 'recreating';
      prompt: ViduV1Element | null;
    };

export interface TempUploadRefs {
  processed: { current: Set<string> };
  pending: { current: Set<string> };
}

export interface TempUploadReplacement {
  from: string;
  to: string;
}

export interface TempUploadItem {
  uploadKey: string;
  run: () => Promise<TempUploadReplacement[] | undefined>;
}

export type FormPromptListItem = {
  id: string;
  prompt?: unknown;
  status?: 'done' | 'uploading';
};

export interface FormPromptListHelpers<T extends FormPromptListItem> {
  updatePromptItem: (promptId: string, updater: (current: T) => T) => void;
  markPromptStatus: (promptId: string, status: 'done' | 'uploading') => void;
}

export type RecaptionPrompt = {
  subject: TextPromptWithInputItem;
  environment: TextPromptWithInputItem;
  style: string[];
  motionLevel: string[];
  shotSize?: string[];
  focalLength?: string[];
  composition?: string[];
  cameraAngle?: string[];
  cameraMovement?: string[];
  specialFilmingTechnique?: string[];
};

/**
 * 从 API 数据类型中提取 query 参数并选择特定字段
 * @template T - API 数据类型（包含 query 字段）
 * @template K - 要选择的字段名称
 */
export type ApiQueryParams<T extends { query?: unknown }, K extends keyof NonNullable<T['query']>> = Pick<
  NonNullable<T['query']>,
  K
>;

export interface VoteListState {
  id: string;
  vote: boolean;
  vote_count: number;
}

/**
 * 文生音频时间片段
 * startTime 和 endTime 都为0表示纯文本
 * startTime 和 endTime 任意一个不为0会渲染成时间戳片段
 */
export type SegmentType = {
  id: string;
  text?: string;
  startTime: number;
  endTime: number;
  color?: string;
};

export enum TOOL_TYPE {
  AI_VIDEO_GENERATOR = 'ai-video-generator',
  AI_ANIMATION_GENERATOR = 'ai-animation-generator',
  AI_IMAGE_ANIMATOR = 'ai-image-animator',
  AI_AD_GENERATOR = 'ai-ad-generator',
  AI_HUG_GENERATOR = 'ai-hug-generator',
  AI_KISSING_GENERATOR = 'ai-kissing-generator',
}

export type ToolType = (typeof TOOL_TYPE)[keyof typeof TOOL_TYPE];

export type ContentItem = {
  title: string;
  description: string;
};

export enum InspirationActiveType {
  Recommended = 'recommended',
  Latest = 'latest',
}
export type MasonryConfig = {
  defaultHeight: number;
  defaultWidth: number;
  spacing: number;
  calculateColumnCount: ({ width, bigLayout }: { width: number; bigLayout?: boolean }) => number;
};

export interface Option<T> {
  label: string;
  value: T;
}

export type SupportedLanguage = 'en' | 'zh' | 'ja' | 'ko' | 'de' | 'fr' | 'es' | 'pt' | 'ru';

/**
 * 方便国际化数据映射
 * @example
 * const i18nData: I18nRecord<string> = {
 *   china: {
 *     zh: '你好',
 *   },
 *   global: {
 *     en: 'Hello',
 *     zh: '你好',
 *     ja: 'こんにちは',
 *   },
 * };
 * 使用: i18nData[SITE][locale]
 */
export type I18nRecord<T> = Record<SiteType, Partial<Record<SupportedLanguage, T>>>;

export type ActivityTemplate = ViduV1GetActivityTemplateReply;

export enum EventStatusEnum {
  ACTIVE = 'active',
  PAUSED = 'paused',
  ENDED = 'ended',
}

export enum ActivityPageFromEnum {
  INSIDE = 'inside',
  OUTSIDE = 'outside',
  INSIDE_BAR = 'inside-bar',
}
export type EventStatus = 'active' | 'paused' | 'ended';

// --- Form state types (extracted from stores/slices/task-form) ---

interface BaseFormParams {
  modelVersion: ModelVersion;
  textPrompt: string;
  imagePrompts: (FormImagePrompt | null)[];
  settingsV2_0: TaskSettingsV2_0;
  settingsQ1: TaskSettingsQ1;
  settingsQ2: TaskSettingsQ2;
  settingsQ2_pro: TaskSettingsQ2_pro;
  settingsQ3_pro: TaskSettingsQ2;
  settingsQ3_fast: TaskSettingsQ2;
  settingsQ3_lite: TaskSettingsQ2;
  settingsGPTImage2: TaskSettingsQ2;
  settingsQ3: TaskSettingsQ3;
  settingsQ3_a: TaskSettingsQ3;
  settingsQ3_hh: TaskSettingsQ3;
  inspirationId?: string;
  inspirationFrom?: string;
  inspirationImageUrl?: string;
  textPromptWithReference?: TextPromptWithInputItem;
  referencePrompts?: ReferencePrompt[];
  isProMode: boolean;
  recaptionPrompt?: RecaptionPrompt;
  isProModeWarningTipVisible?: boolean;
  storyGridSettings?: StoryGridSettings;
}

export interface StoryGridSettings {
  grid_count?: number;
  duration?: number;
  resolution?: string;
  aspect_ratio?: string;
}

export interface Img2VideoFormParams extends BaseFormParams {
  multiFrameTransitions?: MultiFrameTransition[];
}

export type FormParams = BaseFormParams | Img2VideoFormParams;

export interface TemplateFormParams {
  textPrompt: string;
  imagePrompts: (FormImagePrompt | null)[];
  templateId?: string;
  templateScene?: string;
  settings?: TemplateSettings;
  isMergeImage?: boolean;
  maxImageCount?: number;
  creationResolution?: string;
  inspirationId?: string;
  inspirationFrom?: string;
  inspirationImageUrl?: string;
  estimatedDuration?: number;
}

export type Text2AudioFormParams = {
  segments?: SegmentType[];
  settings?: TaskSettingsText2Audio;
};

type NumericGroupData = { total: number; remaining: number; durations?: number[] };

export type Trials = {
  trials720p: NumericGroupData;
  trialsQ1: NumericGroupData;
  trialsQ2TransitionSpeed: NumericGroupData;
  trialsQ2TransitionPro: NumericGroupData;
  trialsQ2T2V: NumericGroupData;
  trialsQ2C2V: NumericGroupData;
  trialsQ2T2I: NumericGroupData;
  trialsQ2R2I: NumericGroupData;
  trialsText2Audio: NumericGroupData;
  /** 声音复刻等「group: voice」试用次数 */
  trialsVoice: NumericGroupData;
  trialsProMode: NumericGroupData;
  trialsQ2Extend: NumericGroupData;
  trialsQ2ExtendPro: NumericGroupData;
  trialsQ2Pro: NumericGroupData;
  trialsQ3T2VI2V: NumericGroupData;
  trialsQ3C2V: NumericGroupData;
  /** 多宫格创作「group: story-grid」试用次数 */
  trialsStoryGrid: NumericGroupData;
  /** 离线数字人「group: offline-avatar」试用次数。只有 /vidu-stream/avatar/offline 消费，
   *  /create/ai-avatar-generator 不走试用。 */
  trialsOfflineAvatar: NumericGroupData;
};

export type FreeCreditsTask = CreditV1NexusHistory & {
  name: FreeCreditsTaskName;
};

export interface FreeCreditsTaskGroups {
  newbieTasks: FreeCreditsTask[];
  advancedCreationTasks: FreeCreditsTask[];
  advertisementTasks: FreeCreditsTask[];
}
/**
 * 活动投稿
 */
export enum ActivityPostEnum {
  SECT_WAR = 'sect_war',
}

export type MentionItem = {
  id: string;
  label: string;
  preview?: string;
  mediaType?: TextPromptMentionMediaType;
  element?: ViduV1Element;
  type?: ReferencePrompt['type'];
  prompt?: ReferencePrompt['prompt'];
};

// 更新共享主体互动状态参数
export type UpdateShareReferenceInteractionParams = {
  id: string;
  traceId?: string;
  voted?: boolean;
  collected?: boolean;
  count?: number;
  collect_count?: number;
};
export type UpdateShareReferenceInteractionFn = (params: UpdateShareReferenceInteractionParams) => void;

// 更新创作互动状态参数
export type UpdateLikeStatusParamsWithCreation = Omit<UpdateInteractionStatusParams, 'obj_type'> & {
  obj_type: UpdateInteractionStatusParams['obj_type'] | 'creation';
};
export type UpdateLikeStatusParamsWithCreationFn = (params: UpdateLikeStatusParamsWithCreation) => void;

export type ConfigItemOption = {
  value: string;
  label: string;
  showVip: boolean;
  remainingTrials?: number;
  icon?: string;
  subPlans?: SubsPlanType[];
  description?: string;
};
export type ConfigItemOptions = ConfigItemOption[];
export type ConfigItem = (string | ConfigItemOption)[];
export type ConfigMap = Record<'transition' | 'duration' | 'amount' | 'resolution', ConfigItem>;
export type FinalConfigMap = Record<'transition' | 'duration' | 'amount' | 'resolution', ConfigItemOptions> & {
  checkOptionValue?: ({ key, value, subsPlan }: { key: string; value: string; subsPlan?: SubsPlanType }) => boolean;
  checkItemMap?: Record<string, Record<string, SubsPlanType[]>>;
};

export type TaskParams = Pick<ViduV1Task, 'input' | 'settings' | 'type' | 'extra_params'> & {
  text?: string;
};

export interface MultiFrameTransition {
  duration: number;
  prompt?: string;
  from: number;
  to: number;
}

export type ImageType = 'image/webp' | 'image/png' | 'image/jpeg' | 'image/bmp';
export interface Detector {
  type: ImageType;
  check: (view: Uint8Array) => boolean;
}

export type ElementModality = Required<ViduV1Element>['modality'];

export enum BackfillTaskType {
  CHARACTER2VIDEO = 'character2video',
  IMG2VIDEO = 'img2video',
  SCENE = 'scene',
  REFERENCE2IMAGE = 'reference2image',
  STYLE_TRANSFER = 'style_transfer',
  LIP_SYNC = 'lip_sync',
  FACE_SWAP = 'face_swap',
  MOTION_CONTROL = 'motion_control',
  STORY_GRID = 'storyGrid',
}

/** 场景：watch_ad_credit(积分不足场景)、watch_ad_watermark(去水印场景)、open_screen_ads(开屏广告场景)、continue_earn_credits(继续观看广告场景)、task_center(任务中心场景) */
export type AdScene =
  | 'watch_ad_credit'
  | 'watch_ad_watermark'
  | 'open_screen_ads'
  | 'continue_earn_credits'
  | 'task_center'
  | 'sidebar_bottom';

export type ChangedImagePrompt = {
  id: string;
  range: ImageRange;
  cropRatio?: number | 'free';
  imagePrompt: ImagePromptWithSize;
  containerSize: { width: number; height: number };
};

/** 用户功能标签的 key 集合，用于在配置中声明内测准入条件 */
export type UserTagKey = 'isExpert';

export type SettingsLanguage = 'auto' | 'zh' | 'en';

export interface EmotionType {
  id: string;
  labelKey: string;
  tagBackgroundColor: string;
  tagTextColor: string;
  emotionTextBackgroundColor: string;
}
